import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BreakRuleRequest,
  BreakRuleWithUsageListItemResponse,
  CustomerLocationService,
  DutyModelRequest,
  DutyModelWithAdoptingDepartmentsListItemResponse,
  ImportUploadResponse,
  RoundingRuleRequest,
  RoundingRuleWithUsageListItemResponse,
} from '../../../api';
import { BASE_SKIP_ERROR_TOAST } from '../../core/constants/base-http-context-tokens';

@Injectable({ providedIn: 'root' })
export class BaseCustomerLocationService {
  constructor(private readonly customerLocationService: CustomerLocationService) {}

  getLocationImportTemplate(customer: number): Observable<Blob> {
    return this.customerLocationService.getCustomerLocationImportTemplate({ customer });
  }

  uploadLocationImport(customer: number, file: File): Observable<ImportUploadResponse | undefined> {
    return this.customerLocationService
      .postCustomerLocationImportUpload({
        customer,
        body: { file },
        context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
      })
      .pipe(map((response) => response.data));
  }

  processLocationImport(customer: number, importId: string): Observable<void> {
    return this.customerLocationService.postCustomerLocationImportProcess({ customer, import: importId });
  }

  getDutyModels(params: {
    customer: number;
    location: number;
  }): Observable<DutyModelWithAdoptingDepartmentsListItemResponse[]> {
    return this.customerLocationService
      .getCustomerLocationDutyModelsList(params)
      .pipe(map((response): DutyModelWithAdoptingDepartmentsListItemResponse[] => response.data || []));
  }

  createDutyModel(params: { customer: number; location: number; body: DutyModelRequest }): Observable<unknown> {
    // The effect decides on the toast: a duplicate short code is shown at the form field instead.
    return this.customerLocationService.postCustomerLocationDutyModelsCreate({
      ...params,
      context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
    });
  }

  updateDutyModel(params: {
    customer: number;
    location: number;
    dutyModel: number;
    body: DutyModelRequest;
  }): Observable<unknown> {
    return this.customerLocationService.putCustomerLocationDutyModelsUpdate({
      ...params,
      context: new HttpContext().set(BASE_SKIP_ERROR_TOAST, true),
    });
  }

  deleteDutyModel(params: { customer: number; location: number; dutyModel: number }): Observable<unknown> {
    return this.customerLocationService.deleteCustomerLocationDutyModelsDelete(params);
  }

  /** Same shape as the break-rule activation: POST / DELETE on one `.../activation` resource. */
  setDutyModelActivation(params: {
    customer: number;
    location: number;
    dutyModel: number;
    active: boolean;
  }): Observable<unknown> {
    const { active, ...pathParams } = params;

    return active
      ? this.customerLocationService.postCustomerLocationDutyModelsActivate(pathParams)
      : this.customerLocationService.deleteCustomerLocationDutyModelsDeactivate(pathParams);
  }

  getBreakRules(params: { customer: number; location: number }): Observable<BreakRuleWithUsageListItemResponse[]> {
    return this.customerLocationService
      .getCustomerLocationBreakRulesList(params)
      .pipe(map((response): BreakRuleWithUsageListItemResponse[] => response.data || []));
  }

  createBreakRule(params: { customer: number; location: number; body: BreakRuleRequest }): Observable<unknown> {
    return this.customerLocationService.postCustomerLocationBreakRulesCreate(params);
  }

  updateBreakRule(params: {
    customer: number;
    location: number;
    breakRule: number;
    body: BreakRuleRequest;
  }): Observable<unknown> {
    return this.customerLocationService.putCustomerLocationBreakRulesUpdate(params);
  }

  deleteBreakRule(params: { customer: number; location: number; breakRule: number }): Observable<unknown> {
    return this.customerLocationService.deleteCustomerLocationBreakRulesDelete(params);
  }

  /** Activation is POST / DELETE on one `.../activation` resource; `active` is the state to move to. */
  setBreakRuleActivation(params: {
    customer: number;
    location: number;
    breakRule: number;
    active: boolean;
  }): Observable<unknown> {
    const { active, ...pathParams } = params;

    return active
      ? this.customerLocationService.postCustomerLocationBreakRulesActivate(pathParams)
      : this.customerLocationService.deleteCustomerLocationBreakRulesDeactivate(pathParams);
  }

  getRoundingRules(params: {
    customer: number;
    location: number;
  }): Observable<RoundingRuleWithUsageListItemResponse[]> {
    return this.customerLocationService
      .getCustomerLocationRoundingRulesList(params)
      .pipe(map((response): RoundingRuleWithUsageListItemResponse[] => response.data || []));
  }

  createRoundingRule(params: { customer: number; location: number; body: RoundingRuleRequest }): Observable<unknown> {
    return this.customerLocationService.postCustomerLocationRoundingRulesCreate(params);
  }

  updateRoundingRule(params: {
    customer: number;
    location: number;
    roundingRule: number;
    body: RoundingRuleRequest;
  }): Observable<unknown> {
    return this.customerLocationService.putCustomerLocationRoundingRulesUpdate(params);
  }

  deleteRoundingRule(params: { customer: number; location: number; roundingRule: number }): Observable<unknown> {
    return this.customerLocationService.deleteCustomerLocationRoundingRulesDelete(params);
  }

  /** Activation is POST / DELETE on one `.../activation` resource; `active` is the state to move to. */
  setRoundingRuleActivation(params: {
    customer: number;
    location: number;
    roundingRule: number;
    active: boolean;
  }): Observable<unknown> {
    const { active, ...pathParams } = params;

    return active
      ? this.customerLocationService.postCustomerLocationRoundingRulesActivate(pathParams)
      : this.customerLocationService.deleteCustomerLocationRoundingRulesDeactivate(pathParams);
  }
}
